import type { Dayjs } from 'dayjs';

import {
  getLocalTimeZone,
  parseAbsolute,
  toCalendarDate,
} from '@internationalized/date';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

export const initDayJSPlugins = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
};

export { dayjs };

// Use fixed 'en-US' locale to prevent SSR hydration mismatches
// (server and client may have different default locales)
const minutesFormatter = new Intl.NumberFormat('en-US', {
  style: 'unit',
  unit: 'minute',
  unitDisplay: 'short',
});

const dateFormatterMedium = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
});

const dateFormatterMonthDay = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

const timeFormatterShort = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

const dateTimeFormatterMedium = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

export const formatDateTime = (
  date: Date | Dayjs | string | undefined,
): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid()
    ? dateTimeFormatterMedium.format(parsed.toDate())
    : '';
};

export const formatMinutes = (minutes: number) =>
  Number.isNaN(minutes) ? '' : minutesFormatter.format(minutes);

/** Formats a minutes range, e.g. "15–25 min"; collapses equal bounds. */
export const formatMinutesRange = (low: number, high: number): string => {
  if (Number.isNaN(low) || Number.isNaN(high)) return '';
  return low === high ? formatMinutes(high) : `${low}–${formatMinutes(high)}`;
};

/**
 * Formats a millisecond duration as e.g. "45s", "13m", or "1h 5m". Durations
 * of a minute or more are rounded UP to the next whole minute; only sub-minute
 * durations show seconds.
 */
export const formatDurationMs = (ms: number): string => {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const totalMinutes = Math.ceil(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

export const formatDate = (date: Date | Dayjs | string | undefined): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid() ? dateFormatterMedium.format(parsed.toDate()) : '';
};

/** Month and day only, e.g. "Sep 6", for layouts too narrow for the year. */
export const formatMonthDay = (
  date: Date | Dayjs | string | undefined,
): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid() ? dateFormatterMonthDay.format(parsed.toDate()) : '';
};

export const formatDateRange = (
  fromDate: Date | Dayjs | string | undefined,
  endDate: Date | Dayjs | string | undefined,
): string => {
  if (!fromDate || !endDate) return '';
  const from = dayjs(fromDate);
  const end = dayjs(endDate);
  if (!from.isValid() || !end.isValid()) return '';
  return `${dateFormatterMonthDay.format(from.toDate())} – ${dateFormatterMonthDay.format(end.toDate())}`;
};

export const formatTime = (date: Date | Dayjs | string | undefined): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid() ? timeFormatterShort.format(parsed.toDate()) : '';
};

export const getDaysFromNowText = (
  date: Date | Dayjs | string | undefined,
): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  const now = dayjs();
  const daysDiff = parsed.diff(now, 'day');
  if (daysDiff < 0) {
    return 'past';
  } else if (daysDiff === 0) {
    return 'today';
  }
  return `in ${daysDiff} days`;
};

const relativeDayFormatter = new Intl.RelativeTimeFormat('en-US', {
  numeric: 'auto',
});

/**
 * How long ago a past moment was, in calendar days in `timeZone`: "today",
 * "yesterday", "3 days ago". Calendar days, not 24-hour blocks, so something
 * done last night reads "yesterday" the next morning. A moment in the future
 * (clock skew between server and device) reads "today".
 */
export const getDaysAgoText = (
  date: Date | Dayjs | string | undefined,
  {
    timeZone = getLocalTimeZone(),
    now = new Date(),
  }: { timeZone?: string; now?: Date } = {},
): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  if (!parsed.isValid()) return '';
  const day = toCalendarDate(parseAbsolute(parsed.toISOString(), timeZone));
  const currentDay = toCalendarDate(parseAbsolute(now.toISOString(), timeZone));
  return relativeDayFormatter.format(
    Math.min(day.compare(currentDay), 0),
    'day',
  );
};
